import * as React from "react";

import { currencyFormatter } from "../../lib/common";
import { ProductContext, ProductContextProvider } from "./product-context";
import { AttributeOption, Product, ProductCartModel } from "./types";
import { AttributeSelector } from "./AttributeSelector";

import "../../css/productDetail.scss";
import { AddToCartButton } from "./AddToCartButton";
import { QuantityEditor } from "./QuantityEditor";
import { calculatePrice } from "./calculatePrice";
import { createModel, validateModel } from "./validateModel";
import { postAddToCart } from "./postAddToCart";

export const ProductDetailView: React.FunctionComponent<{
  item: Product;
}> = (props) => {
  const [productSelected, setProductSelected] =
    React.useState<ProductCartModel>(createModel(props.item));

  const [quantity, setQuantity] = React.useState(1);
  const [enhanced, setEnhanced] = React.useState(false);

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [isValidated, setIsValidated] = React.useState(false);
  const [revalidateOnChange, setRevalidateOnChange] = React.useState(false);

  const validate = React.useCallback(() => {
    const errs = validateModel(props.item, productSelected);
    setErrors(errs);
    setIsValidated(true);
    setRevalidateOnChange(true);
    return Object.keys(errs).length === 0;
  }, [props.item, productSelected]);

  const setAttribute = (option: AttributeOption) => {
    const selectedAttributes = [...productSelected.Attributes];
    const existingAttribute = selectedAttributes.find(
      (attribute) => attribute.AttributeId === option.AttributeId
    );

    if (existingAttribute) {
      existingAttribute.DetailId = option.Id;
      existingAttribute.Name = option.Name;
    } else {
      selectedAttributes.push({
        DetailId: option.Id,
        Name: option.Name,
        AttributeId: option.AttributeId,
      });
    }

    //if (option.Price > 0) {
    const allOptions = props.item.AttributeOptions.flatMap(
      (attr) => attr.Options
    );

    const attributesFull = selectedAttributes
      .map((sel) => allOptions.find((opt) => opt.Id === sel.DetailId))
      .filter((opt): opt is AttributeOption => opt !== undefined);

    productSelected.Price = calculatePrice(attributesFull, props.item.Price);
    //}

    setProductSelected({
      ...productSelected,
      Attributes: selectedAttributes,
    });
  };

  const onClickHandler = async () => {
    if (validate()) {
      postAddToCart({ ...productSelected, Quantity: quantity })
        .then((v) => {
          if (v) {
            setErrors({ submit: "Unable to Add to Cart: " + v });
            return;
          }
          document.location.href = "/ShoppingCart.aspx";
        })
        .catch(() => {
          setErrors({ submit: "Unable to Add to Cart." });
        });
    }
  };

  const productContext: ProductContext = {
    product: props.item,
    setAttribute: setAttribute,
    errors,
    isValidated,
    validate,
    setErrors,
    revalidateOnChange,
    setRevalidateOnChange,
  };

  let displayPrice: string | JSX.Element = currencyFormatter.format(
    productSelected.Price
  );
  if (productSelected.Price !== productSelected.OriginalPrice) {
    displayPrice = (
      <>
        <span className="original-price">
          {currencyFormatter.format(productSelected.OriginalPrice)}
        </span>
        {currencyFormatter.format(productSelected.Price)}
      </>
    );
  }
  const nonSalePrice = currencyFormatter.format(productSelected.OriginalPrice);

  React.useEffect(() => {
    // This runs AFTER hydration
    setEnhanced(true);
  }, []);

  React.useEffect(() => {
    if (revalidateOnChange) {
      validate();
    }
  }, [productSelected, validate, revalidateOnChange]);

  const isActive = props.item.IsActive;

  return (
    <ProductContextProvider value={productContext}>
      <div className="price-container">
        {!enhanced && <span className="price">{nonSalePrice}</span>}
        {enhanced && (
          <>
            <span className="price">{displayPrice}</span>
            <span className="loris-app-only fee-message">
              + 10% processing fee
            </span>
          </>
        )}
      </div>

      {enhanced && isActive && (
        <div id="pnlCartControls" className="cart-controls">
          <QuantityEditor
            quantity={quantity}
            setQuantity={setQuantity}
          ></QuantityEditor>
        </div>
      )}

      {enhanced && isActive && (
        <div
          className={`product-attributes ${
            productContext.isValidated ? "validated" : ""
          }`}
        >
          <AttributeSelector
            attributes={productContext.product.AttributeOptions}
          ></AttributeSelector>
        </div>
      )}

      {enhanced && isActive && (
        <div className="addToCart">
          {(!isValidated ||
            (isValidated && Object.entries(errors).length === 0)) && (
            <AddToCartButton
              item={props.item}
              onProductAddToCartClick={onClickHandler}
            ></AddToCartButton>
          )}
          {isValidated && Object.entries(errors).length !== 0 && (
            <div
              className="prod-button disabled"
              title="Please review the options before adding this product to the cart"
            >
              Review Options
            </div>
          )}
        </div>
      )}
      {enhanced && !isActive && (
        <div style={{ gridArea: "error" }}>
          <h3>We are sorry, this product is no longer in stock.</h3>
        </div>
      )}
      {errors["submit"] && <span className="error">{errors["submit"]}</span>}
    </ProductContextProvider>
  );
};
